<script setup lang="ts">
useSeoMeta({
  title: 'Explore by Need — QikSolve',
  description:
    'Explore QikSolve questions about practical quality systems, regulation, and regulated operations on Microsoft 365.',
})

const pillars = [
  {
    eyebrow: 'If an Excel register is central to quality oversight',
    title: 'Validate an Excel quality register proportionately',
    question: 'How can we validate an Excel quality register and decide what to do next?',
    description:
      'Work through intended use, criticality, controls, evidence, and the choice between retaining, remediating, or migrating the register.',
    contexts: ['GMP / GxP', 'Quality records', 'Validation evidence'],
    to: '/explore-by-need/validate-excel-quality-register',
  },
  {
    eyebrow: 'If you need to turn regulation into daily practice',
    title: 'Make quality and compliance workable for your team',
    question: 'How do you turn regulatory expectations into workable daily practice?',
    description:
      'Examine the people, processes, systems, ownership, and evidence needed to make regulated quality practice workable and sustainable.',
    contexts: ['GMP / GxP', 'FDA 21 CFR Part 11', 'EU Annex 11'],
    to: '/product/regulatory-compliance',
  },
  {
    eyebrow: 'If you need a quality system your team can sustain',
    title: 'Build a controlled quality system people can use',
    question: 'How can a controlled quality system remain usable alongside daily quality work?',
    description:
      'Consider the capacity, records, validation, administration, and ongoing control required to sustain a practical quality system.',
    contexts: ['GMP / GxP', 'FDA 21 CFR Part 11', 'EU Annex 11'],
    to: '/product/quality-systems',
  },
  {
    eyebrow: 'If your quality problems cross team and system boundaries',
    title: 'Find where quality problems are really created',
    question: 'How do people, process, information, and decisions work together across your operation?',
    description:
      'Trace quality outcomes across the operating system and examine where clearer ownership, flow, and feedback can support repeatable practice.',
    contexts: ['GMP / GxP', 'ISO 9001'],
    to: '/product/systems-thinking-for-regulated-operations',
  },
  {
    eyebrow: 'If ISO 9001 is becoming important to your next stage of growth',
    title: 'Build a quality culture that supports sustainable growth',
    question: 'How can you meet customer expectations, expand sustainably, and keep quality work lean and clear?',
    description:
      'Whether ISO 9001 is expected by a major customer or government buyer, supports expansion, or helps clarify your quality culture, connect the standard to the way your organisation actually works.',
    contexts: ['ISO 9001'],
    to: '/product/iso-9001',
  },
  {
    eyebrow: 'If generic systems are forcing your team to compromise',
    title: 'Choose a proportionate path to workable capability',
    question: 'How should you assess fit before choosing a QikSolve pathway?',
    description:
      'Start with the work, ownership, records, evidence, requirements, and capacity that shape a workable quality or operational solution.',
    contexts: ['Quality operations', 'Digital practice', 'QikSolve pathways'],
    to: '/explore-by-need/choose-a-proportionate-path-to-workable-capability',
  },
]

const serviceFits = [
  {
    title: 'Paper-to-Digital Quality Transformation',
    problem: 'Quality records and decisions are fragmented across paper, Word, Excel, and email.',
    fitQuestion: 'Do we need a staged transition model that improves control without disrupting current operations?',
    contexts: ['Practical GxP quality systems', 'Quality records', 'Digital practice'],
    to: '/explore-by-need/paper-to-digital-quality-transformation',
  },
  {
    title: 'Software Implementation, Maintenance and Management',
    problem: 'Our platform works, but change, maintenance, and governance are becoming inconsistent or reactive.',
    fitQuestion: 'Do we need a controlled operating model for implementation, support, and ongoing release decisions?',
    contexts: ['QikSolve solutions and capability', 'Controlled change', 'Operational continuity'],
    to: '/explore-by-need/software-implementation-maintenance-and-management',
  },
  {
    title: 'Quality Process Mapping, Development and Implementation',
    problem: 'Current process ownership and handoffs are unclear, creating variation and avoidable rework.',
    fitQuestion: 'Do we need structured process mapping and implementation support before selecting tooling changes?',
    contexts: ['Systems thinking for regulated operations', 'Process ownership', 'Implementation readiness'],
    to: '/explore-by-need/quality-process-mapping-development-and-implementation',
  },
  {
    title: 'SharePoint Governance',
    problem: 'Microsoft 365 structures and permissions are not consistently aligned with quality ownership and records.',
    fitQuestion: 'Do we need a clearer governance and configuration model for controlled information practice?',
    contexts: ['Regulation to practice', 'Microsoft 365 governance', 'Controlled records'],
    to: '/product/sharepoint-governance',
  },
  {
    title: 'Regulatory Compliance, Validation and CSV Support',
    problem: 'Validation and compliance work is hard to translate into a practical delivery model and evidence set.',
    fitQuestion: 'Do we need support defining proportionate validation, CSV scope, and review-ready evidence?',
    contexts: ['Regulation to practice', 'CSV', 'Validation evidence'],
    to: '/product/regulatory-compliance',
  },
  {
    title: 'Quality Dashboards',
    problem: 'Operational and quality data exists, but reporting does not consistently support decision-making.',
    fitQuestion: 'Do we need dashboard and reporting design focused on the review decisions our team must make?',
    contexts: ['Practical GxP quality systems', 'Management review', 'Quality insight'],
    to: '/product/quality-dashboards',
  },
  {
    title: 'ISO 9001',
    problem: 'Leadership needs a practical ISO 9001 pathway that supports day-to-day operation and growth.',
    fitQuestion: 'Do we need implementation guidance that connects ISO 9001 requirements to real operating practice?',
    contexts: ['ISO 9001 in practice', 'Quality culture', 'Sustainable growth'],
    to: '/product/iso-9001',
  },
  {
    title: 'Agentic AI Governance',
    problem: 'AI use cases are emerging, but boundaries, oversight, and evidence expectations are not yet defined.',
    fitQuestion: 'Do we need a risk-tiered governance model before introducing AI into quality workflows?',
    contexts: ['Practical, governed AI', 'Human oversight', 'Evidence and traceability'],
    to: '/product/agentic-ai-governance',
  },
  {
    title: 'Training and Enablement',
    problem: 'Teams have new quality-system responsibilities, but capability and consistency vary by role.',
    fitQuestion: 'Do we need role-based enablement to sustain SharePoint, CSV, and AI-governance practices?',
    contexts: ['Practical GxP quality systems', 'Capability uplift', 'Adoption'],
    to: '/product/training',
  },
]
</script>

<template>
  <div>
    <section
      id="hero"
      class="relative overflow-hidden bg-gradient-to-br from-brand-500 via-brand-700 to-brand-900 text-white"
    >
      <div class="absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_20%_20%,white,transparent_35%),radial-gradient(circle_at_80%_60%,white,transparent_30%)]" />
      <div class="relative max-w-5xl mx-auto px-4 py-24 text-center">
        <span class="mb-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-sm font-medium ring-1 ring-white/20">
          <span
            class="size-2 rounded-full bg-white/80"
            aria-hidden="true"
          />
          Explore by Need
        </span>
        <h1 class="mb-5 text-4xl font-heading font-extrabold leading-tight tracking-tight sm:text-5xl">
          Start with the quality question your team needs to answer.
        </h1>
        <p class="mx-auto max-w-3xl text-lg leading-8 text-brand-100">
          Navigating complex regulatory and compliance needs alongside competing operational pressures is difficult
          for any regulated business. Explore the questions QikSolve helps regulated teams work through, from
          making more of Microsoft 365 to preparing for validation and improving quality workflows.
        </p>
      </div>
    </section>

    <section class="mx-auto max-w-6xl px-4 py-16 sm:py-20">
      <div class="max-w-3xl">
        <p class="text-sm font-semibold uppercase tracking-wider text-brand-600">
          Choose a question
        </p>
        <h2 class="mt-2 text-3xl font-heading font-bold text-gray-900 sm:text-4xl">
          Choose the question closest to the situation you are facing.
        </h2>
        <p class="mt-4 text-lg leading-8 text-gray-600">
          Each pathway provides the practical framing for one question and points to the detailed corporate guide.
          The index stays focused on the question; the linked hub owns the deeper guidance and next steps.
        </p>
      </div>

      <div class="mt-10 grid gap-6 lg:grid-cols-3">
        <article
          v-for="pillar in pillars"
          :key="pillar.to"
          class="flex flex-col rounded-2xl border border-gray-200 bg-white p-7 shadow-[var(--shadow-card)]"
        >
          <p class="min-h-10 text-sm font-semibold leading-5 text-brand-700">
            {{ pillar.eyebrow }}
          </p>
          <h3 class="mt-3 text-2xl font-heading font-bold leading-tight text-gray-900">
            {{ pillar.title }}
          </h3>
          <p class="mt-4 text-lg font-semibold leading-7 text-gray-800">
            {{ pillar.question }}
          </p>
          <p class="mt-3 text-gray-600">
            {{ pillar.description }}
          </p>
          <ul
            class="mt-5 flex flex-wrap gap-2 border-t border-gray-100 pt-4"
            aria-label="Relevant context where applicable"
          >
            <li
              v-for="context in pillar.contexts"
              :key="context"
              class="rounded-full bg-gray-50 px-3 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-200"
            >
              {{ context }}
            </li>
          </ul>
          <NuxtLink
            :to="pillar.to"
            class="mt-7 inline-flex items-center gap-2 font-semibold text-brand-700 hover:text-brand-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
          >
            Explore this question
            <UIcon
              name="i-heroicons-arrow-right-20-solid"
              class="size-4"
              aria-hidden="true"
            />
          </NuxtLink>
        </article>
      </div>
    </section>

    <section class="mx-auto max-w-6xl px-4 pb-16 sm:pb-20">
      <div class="max-w-3xl">
        <p class="text-sm font-semibold uppercase tracking-wider text-brand-600">
          Service fit assessment
        </p>
        <h2 class="mt-2 text-3xl font-heading font-bold text-gray-900 sm:text-4xl">
          Match your current problem to the right service pathway.
        </h2>
        <p class="mt-4 text-lg leading-8 text-gray-600">
          Each service pathway includes a core problem statement and a fit question to help you decide where to begin.
        </p>
      </div>

      <div class="mt-10 grid gap-6 lg:grid-cols-3">
        <article
          v-for="service in serviceFits"
          :key="service.to"
          class="flex flex-col rounded-2xl border border-gray-200 bg-white p-7 shadow-[var(--shadow-card)]"
        >
          <h3 class="text-xl font-heading font-bold leading-tight text-gray-900">
            {{ service.title }}
          </h3>
          <p class="mt-4 text-sm font-semibold uppercase tracking-wide text-brand-700">
            Problem
          </p>
          <p class="mt-2 text-gray-700">
            {{ service.problem }}
          </p>
          <p class="mt-4 text-sm font-semibold uppercase tracking-wide text-brand-700">
            Fit question
          </p>
          <p class="mt-2 text-gray-800">
            {{ service.fitQuestion }}
          </p>
          <ul
            class="mt-5 flex flex-wrap gap-2 border-t border-gray-100 pt-4"
            aria-label="Relevant pillar and context"
          >
            <li
              v-for="context in service.contexts"
              :key="context"
              class="rounded-full bg-gray-50 px-3 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-200"
            >
              {{ context }}
            </li>
          </ul>
          <NuxtLink
            :to="service.to"
            class="mt-7 inline-flex items-center gap-2 font-semibold text-brand-700 hover:text-brand-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
          >
            Explore this need
            <UIcon
              name="i-heroicons-arrow-right-20-solid"
              class="size-4"
              aria-hidden="true"
            />
          </NuxtLink>
        </article>
      </div>
    </section>

    <section class="border-y border-brand-100 bg-brand-50/70">
      <div class="mx-auto flex max-w-5xl flex-col gap-5 px-4 py-12 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 class="text-2xl font-heading font-bold text-gray-900">
            Need help deciding where to begin?
          </h2>
          <p class="mt-2 text-gray-600">
            Bring your quality or operational question to a discovery conversation.
          </p>
        </div>
        <UButton
          to="/contact"
          trailing-icon="i-heroicons-arrow-right-20-solid"
          class="shrink-0 bg-brand-600 text-white hover:bg-brand-700"
        >
          Book a discovery call
        </UButton>
      </div>
    </section>
  </div>
</template>
